import React, { PropsWithChildren, useEffect, useMemo } from "react";
import { ApolloNextAppProvider, ApolloClient } from "@apollo/experimental-nextjs-app-support";
import { ApolloProvider, NormalizedCacheObject } from "@apollo/client";
import { useRouter, useSearchParams } from "next/navigation";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import useManageAuth from "@/redux/slice/auth/hooks/useManageAuth";
import { isBrowser } from "@/libs/helpers";
import authTokenSelector from "@/redux/slice/auth/selectors/authToken";
import tokenRefreshSelector from "@/redux/slice/auth/selectors/tokenRefresh";
import signedInStatusSelector from "@/redux/slice/auth/selectors/signedInState";
import buildApolloClient from "@/redux/slice/auth/buildApolloClient";
import authDataSelector from "@/redux/slice/auth/selectors/consentAuthData";
import performSignOutUpdates from "@/redux/slice/auth/actions/performSignOutUpdates";
import { SignedinState } from "@/redux/slice/auth/enums/SignedInState";
import { SignOutActions } from "@/redux/slice/auth/types/SignOutActions";
import authLogout from "@/redux/slice/auth/actions/authLogout";
import { updateTokenRecievedFetchPatients } from "@/redux/slice/auth/slice";
import firebaseRepo from "@/libs/firebase";
import { DeviceInfo } from "@/interface/deviceInfo";

interface AuthManagerProps {
  ua?: DeviceInfo;
  xAuthToken?: string;
  xMobileNumber?: string;
}

const AuthManager: React.FC<PropsWithChildren<AuthManagerProps>> = ({ children, ua, xAuthToken, xMobileNumber }) => {
  const dispatch = useAppDispatch();
  const tokenRefresh = useAppSelector(tokenRefreshSelector);
  const authToken = useAppSelector(authTokenSelector);
  const searchParams = useSearchParams();
  const router = useRouter();
  const signedInStatus = useAppSelector(signedInStatusSelector);
  const authData = useAppSelector(authDataSelector);

  const handleLogout = () => {
    try {
      apolloClient.clearStore();
      performSignOutUpdates({ dispatch });
    } catch (error) {
      console.error("Error in handleLogout");
    }
  };

  useEffect(() => {
    const token = searchParams.get("t");
    const utm_source = searchParams.get("utm_source");

    if (token && utm_source && signedInStatus === SignedinState.SIGNEDOUT) {
      // router.push(clientRoutes.signIn(""));
    }
  }, []);

  useEffect(() => {
    !authData?.askForConsent && authData?.reason === "NOT_VERIFIED" && handleLogout();
  }, [authData, signedInStatus]);

  const handleUnAuthenticate = () => {
    const signOutActions: SignOutActions = {
      dispatchSignOutUpdates() {
        authLogout({ apolloClient, dispatch });
      },
    };
    firebaseRepo.getInstance().signOut(signOutActions);
  };

  const fetchPatients = () => {
    dispatch(updateTokenRecievedFetchPatients(true));
  };

  const apolloClient: ApolloClient<NormalizedCacheObject> = useMemo(
    () => buildApolloClient(authToken, tokenRefresh, fetchPatients, handleUnAuthenticate),
    [authToken]
  );

  useManageAuth(dispatch, apolloClient, ua, xAuthToken, xMobileNumber);

  return !isBrowser() ? (
    <ApolloNextAppProvider makeClient={() => apolloClient}>{children}</ApolloNextAppProvider>
  ) : (
    <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
  );
};

export default AuthManager;
